import { CloseIcon } from "../../../icons/monochrome";
import classNames from "classnames";
import styles from "./Select.module.scss";
import { Span } from "../Typography";
import { Tooltip } from "../../index";
import React, { useLayoutEffect, useRef, useState } from "react";
import _ from "lodash";

const RemoveButton = ({ unselectOption }) => {
    return <button onClick={(e) => {
        e.__multiSelectRemove = true;

        /**
         * Some wierd propagation shit is happening, so if the click isn't within the button, don't handle it
         */
        const boundingRect = e.target.getBoundingClientRect();
        if (e.clientX < boundingRect.x || e.clientX > boundingRect.x + boundingRect.width || e.clientY < boundingRect.y || e.clientY > boundingRect.y + boundingRect.height) {
            return;
        }

        /**
         * Its in a timeout so that the element only gets removed after the click finishes handling,
         * otherwise the element gets removed, and the click triggers on the chip that takes its place
         */
        setTimeout(() => {
            unselectOption();
        })
    }}><CloseIcon/></button>
}

const MultiSelectedOptionChip = ({ option, unselectOption, style, isClipped, isRequireAcceptOnChange }) => {
    const noRemove = isRequireAcceptOnChange || option.noRemove;
    return <div className={classNames([styles["multi-value-chip"], { [styles["no-remove"]]: noRemove }])} style={style}>
        <Span ellipsis color="on-color">{option.label || option.value}</Span>
        {!noRemove && <RemoveButton unselectOption={() => unselectOption(option)}/>}
        {isClipped && <Tooltip>{option.label || option.value}</Tooltip>}
    </div>
}

export const MultiSelectedOptionsRenderer = React.memo(({ selectionState, unselectOption, isRequireAcceptOnChange }) => {
    const containerRef = useRef(null);
    const remainderChipRef = useRef(null);
    const [overflowState, setOverflowState] = useState(null);
    const previousSelectedOptions = useRef(null);

    useLayoutEffect(() => {
        const remainderChipMinSize = 55;
        const viableChipMinSize = 60;
        const rightBound = containerRef.current.clientWidth;

        let clippedChildIndex = null;
        let currentChild = containerRef.current.lastChild;
        let collapsedCount = 0;
        let index = selectionState.selectedOptions.length - 1;

        if (currentChild === remainderChipRef.current) {
            currentChild = currentChild.previousSibling;
        }

        while (!clippedChildIndex && currentChild) {
            if (currentChild.offsetLeft >= rightBound) { //Completely out
                collapsedCount = collapsedCount + 1;
            } else if (currentChild.offsetLeft + currentChild.offsetWidth >= rightBound) { //clipped
                const visibleSize = rightBound - currentChild.offsetLeft;
                if (collapsedCount > 0) {
                    if (visibleSize > viableChipMinSize + remainderChipMinSize) {
                        clippedChildIndex = index;
                    } else {
                        collapsedCount = collapsedCount + 1;
                    }
                } else if (visibleSize > viableChipMinSize) {
                    clippedChildIndex = index
                } else {
                    collapsedCount = collapsedCount + 1;
                }
            } else if (currentChild.offsetLeft + currentChild.offsetWidth >= rightBound - remainderChipMinSize) { //clipped cause of remainder chip
                const visibleSize = rightBound - remainderChipMinSize - currentChild.offsetLeft;

                if (visibleSize < viableChipMinSize) {
                    collapsedCount = collapsedCount + 1;
                } else {
                    clippedChildIndex = index;
                }
            }

            currentChild = currentChild.previousSibling;
            index = index - 1;
        }

        previousSelectedOptions.current = selectionState.selectedOptions;
        setOverflowState({
            clippedChildIndex,
            collapsedCount
        })
    }, [selectionState.selectedOptions]);

    const isCalculating = previousSelectedOptions.current !== selectionState.selectedOptions;
    const remainingOptions = overflowState?.collapsedCount > 0 ? _.takeRight(selectionState.selectedOptions, overflowState?.collapsedCount) : [];

    return <div className={styles["multi-value"]} ref={containerRef}>
        {selectionState.selectedOptions.map((option, index) => {
            let style = undefined;
            let isClipped = false;

            if (!overflowState || isCalculating) {
                style = { opacity: 0 }
            } else if (index >= selectionState.selectedOptions.length - overflowState.collapsedCount) {
                style = { display: "none" }
            } else if (index === overflowState.clippedChildIndex) {
                style = { overflow: "hidden" }
                isClipped = true;
            }

            return <MultiSelectedOptionChip isRequireAcceptOnChange={isRequireAcceptOnChange} style={style} isClipped={isClipped} unselectOption={unselectOption} key={option.value?.id || option.value} option={option}/>
        })}

        {remainingOptions.length > 0 && <div className={styles["multi-value-remainder-chip"]} ref={remainderChipRef}>
            +{remainingOptions.length}
            <Tooltip contentClassName={styles["multi-value-remainder-tooltip-content"]}>
                {remainingOptions.map(option => {
                    const noRemove = isRequireAcceptOnChange || option.noRemove;

                    return <React.Fragment key={option.value?.id || option.value}>
                        <Span ellipsis level={13}>{option.label || option.value}</Span>
                        {!noRemove && <RemoveButton unselectOption={() => unselectOption(option)}/>}
                    </React.Fragment>
                })}
            </Tooltip>
        </div>}
    </div>
})

export const SelectedOptionRenderer = ({ selectionState, showSecondaryLabelInSelectedOption }) => {
    const { label, value, secondaryLabel } = selectionState.selectedOptions[0];

    return <div className={styles["single-value"]}>
        {label || value}{showSecondaryLabelInSelectedOption && secondaryLabel && ` | ${secondaryLabel}`}
    </div>
}